import React, { useEffect, useState } from "react";
import { DashboardCardInput } from "src/components/shared/DashboardCardInput";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import type { DashboardCard } from "src/types/dashboard";

interface KnowledgeResourceDynamicFieldsProps {
  resourceType: string;
  values: {
    name: string;
    description: string;
    baseQueryFilter: string;
    dashboardIds: string;
    urls: string;
    notionLinks: string;
    confluenceLinks: string;
  };
  onChange: {
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onBaseQueryFilterChange: (value: string) => void;
    onDashboardIdsChange: (value: string) => void;
    onUrlsChange: (value: string) => void;
    onNotionLinksChange: (value: string) => void;
    onConfluenceLinksChange: (value: string) => void;
  };
  className?: string;
  productName?: string;
}

// Helper function to detect if a value is a URL
const isUrl = (value: string): boolean => {
  return value.trim().startsWith("http://") || value.trim().startsWith("https://");
};

// Convert comma-separated string to cards with unique IDs
const stringToCards = (value: string, prefix: string = "card"): DashboardCard[] => {
  if (!value.trim()) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v)
    .map((v, index) => ({
      id: `${prefix}-${Date.now()}-${index}`,
      value: v,
      type: isUrl(v) ? "url" : "id",
    }));
};

// Convert cards to comma-separated string
const cardsToString = (cards: DashboardCard[]): string => {
  return cards.map((card) => card.value).join(", ");
};

export function KnowledgeResourceDynamicFields({
  resourceType,
  values,
  onChange,
  className = "bg-card border-border",
  productName = "TierZero"
}: KnowledgeResourceDynamicFieldsProps) {
  const { name, description, baseQueryFilter, dashboardIds, urls, notionLinks, confluenceLinks } = values;
  const {
    onNameChange,
    onDescriptionChange,
    onBaseQueryFilterChange,
    onDashboardIdsChange,
    onUrlsChange,
    onNotionLinksChange,
    onConfluenceLinksChange
  } = onChange;

  // State for managing cards (combining IDs and URLs)
  const [dashboardCards, setDashboardCards] = useState<DashboardCard[]>([]);
  const [notionCards, setNotionCards] = useState<DashboardCard[]>([]);
  const [confluenceCards, setConfluenceCards] = useState<DashboardCard[]>([]);

  // Initialize cards from comma-separated strings when resource type or values change
  useEffect(() => {
    if (resourceType === "DATADOG_DASHBOARD" || resourceType === "DATADOG_NOTEBOOK" || resourceType === "NEW_RELIC_DASHBOARD" || resourceType === "GRAFANA_CLOUD_DASHBOARD" || resourceType === "GCP_DASHBOARD") {
      const idsCards = stringToCards(dashboardIds, "id");
      const urlCards = stringToCards(urls, "url");
      setDashboardCards([...idsCards, ...urlCards]);
    } else if (resourceType === "NOTION_LINK") {
      setNotionCards(stringToCards(notionLinks, "notion"));
    } else if (resourceType === "CONFLUENCE_LINK") {
      setConfluenceCards(stringToCards(confluenceLinks, "confluence"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType]); // Only run on resource type change to avoid infinite loops

  // Handle dashboard/notebook cards change
  const handleDashboardCardsChange = (cards: DashboardCard[]) => {
    setDashboardCards(cards);

    // Split cards into IDs and URLs for backend
    const ids = cards.filter((c) => c.type === "id").map((c) => c.value).join(", ");
    const urlList = cards.filter((c) => c.type === "url").map((c) => c.value).join(", ");

    onDashboardIdsChange(ids);
    onUrlsChange(urlList);
  };

  // Handle notion cards change
  const handleNotionCardsChange = (cards: DashboardCard[]) => {
    setNotionCards(cards);
    onNotionLinksChange(cardsToString(cards));
  };

  // Handle confluence cards change
  const handleConfluenceCardsChange = (cards: DashboardCard[]) => {
    setConfluenceCards(cards);
    onConfluenceLinksChange(cardsToString(cards));
  };

  switch (resourceType) {
    case "DATADOG_DASHBOARD":
      return (
        <div>
          <Label>Dashboard ID or URL</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">
            Enter dashboard IDs or full URLs.
          </div>
          <DashboardCardInput
            placeholder="Enter dashboard ID or URL..."
            className={className}
            cards={dashboardCards}
            onChange={handleDashboardCardsChange}
            resourceType="dashboard"
          />
        </div>
      );

    case "DATADOG_NOTEBOOK":
      return (
        <div>
          <Label>Notebook ID or URL</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">
            Enter notebook IDs or full URLs.
          </div>
          <DashboardCardInput
            placeholder="Enter notebook ID or URL..."
            className={className}
            cards={dashboardCards}
            onChange={handleDashboardCardsChange}
            resourceType="notebook"
          />
        </div>
      );

    case "NOTION_LINK":
      return (
        <div>
          <Label>Notion Links</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">
            Enter Notion page or database URL link.
          </div>
          <DashboardCardInput
            placeholder="Enter Notion link..."
            className={className}
            cards={notionCards}
            onChange={handleNotionCardsChange}
            resourceType="notion"
          />
        </div>
      );

    case "CONFLUENCE_LINK":
      return (
        <div>
          <Label>Confluence Links</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">
            Enter Confluence page URLs to ingest. {productName} will fetch and process the content from these pages,
            as well as all child pages up to 2 levels of depth.
          </div>
          <DashboardCardInput
            placeholder="Enter Confluence link..."
            className={className}
            cards={confluenceCards}
            onChange={handleConfluenceCardsChange}
            resourceType="notion"
          />
        </div>
      );

    case "DATADOG_LOG_TYPE":
      return (
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="Log type name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Description of the log type"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
          <div>
            <Label htmlFor="baseQueryFilter">Base Query Filter *</Label>
            <Textarea
              id="baseQueryFilter"
              placeholder="@service:web-app @env:production"
              value={baseQueryFilter}
              onChange={(e) => onBaseQueryFilterChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
        </div>
      );

    case "DATADOG_EVENT_TYPE":
      return (
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="Event type name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Description of the event type"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
          <div>
            <Label htmlFor="baseQueryFilter">Base Query Filter *</Label>
            <Textarea
              id="baseQueryFilter"
              placeholder="source:kubernetes @evt.name:Deployment"
              value={baseQueryFilter}
              onChange={(e) => onBaseQueryFilterChange(e.target.value)}
              className={`mt-2 ${className} text-foreground placeholder:text-muted-foreground`}
              required
            />
          </div>
        </div>
      );

    case "NEW_RELIC_DASHBOARD":
      return (
        <div>
          <Label>Dashboard ID or URL</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">
            Enter dashboard IDs or full URLs.
          </div>
          <DashboardCardInput
            placeholder="Enter dashboard ID or URL..."
            className={className}
            cards={dashboardCards}
            onChange={handleDashboardCardsChange}
            resourceType="dashboard"
          />
        </div>
      );

    case "GRAFANA_CLOUD_DASHBOARD":
      return (
        <div>
          <Label>Grafana Dashboard ID or URL</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">Enter Grafana dashboard IDs or full URLs.</div>
          <DashboardCardInput placeholder="Enter Grafana dashboard ID or URL..." className={className} cards={dashboardCards} onChange={handleDashboardCardsChange} resourceType="dashboard" />
        </div>
      );

    case "GCP_DASHBOARD":
      return (
        <div>
          <Label>GCP Cloud Monitoring Dashboard ID or URL</Label>
          <div className="text-sm text-muted-foreground mt-1 mb-2">Enter GCP Cloud Monitoring dashboard IDs or full URLs.</div>
          <DashboardCardInput placeholder="Enter GCP Cloud Monitoring dashboard ID or URL..." className={className} cards={dashboardCards} onChange={handleDashboardCardsChange} resourceType="dashboard" />
        </div>
      );

    default:
      return (
        <div className="text-sm text-muted-foreground p-4 text-center">
          Select a data source to see required fields
        </div>
      );
  }
}